import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ChevronLeft, Share2, Twitter, Facebook, Linkedin } from "lucide-react";
import { PaisleyDivider } from "../components/Paisley";
import { getBlogPost, listBlog } from "../lib/api";

export default function BlogDetail() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [others, setOthers] = useState([]);

  useEffect(() => {
    setPost(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
    getBlogPost(slug).then(setPost).catch(() => {});
    listBlog().then((all) => setOthers(all.filter((p) => p.slug !== slug).slice(0, 3)));
  }, [slug]);

  if (!post) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 animate-pulse">
        <div className="h-8 bg-[#b8af92] w-2/3 mb-4" />
        <div className="h-4 bg-[#b8af92] w-1/3 mb-12" />
        <div className="aspect-[16/10] bg-[#b8af92]" />
      </div>
    );
  }

  return (
    <article className="pb-20" data-testid={`blog-post-${post.slug}`}>
      <div className="max-w-4xl mx-auto px-6 md:px-8 pt-6">
        <Link to="/blog" className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-[#4A4A4A] hover:text-[#1A1A1A]">
          <ChevronLeft size={14} /> Back to Journal
        </Link>
      </div>

      {/* Header */}
      <header className="max-w-3xl mx-auto px-6 pt-12 pb-10 text-center">
        <p className="eyebrow text-[#1A1A1A]">{post.category}</p>
        <h1 className="font-serif-display text-4xl md:text-6xl mt-5 leading-[1.02]" data-testid="blog-title">
          {post.title}
        </h1>
        <div className="flex items-center justify-center gap-4 mt-8 text-xs text-[#7A7A7A] tracking-wider">
          <span>By {post.author}</span>
          <span>·</span>
          <span>{new Date(post.published_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}</span>
          <span>·</span>
          <span>{post.read_minutes} min read</span>
        </div>
      </header>

      {/* Cover */}
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <img src={post.cover} alt={post.title} className="w-full aspect-[16/9] object-cover" />
      </div>

      {/* Body with side share */}
      <div className="max-w-6xl mx-auto px-6 md:px-12 mt-14 grid md:grid-cols-[60px_1fr] gap-8">
        <aside className="hidden md:flex flex-col items-center gap-5 sticky top-28 self-start pt-8">
          <Share2 size={16} className="text-[#7A7A7A]" />
          <a href="#" aria-label="Share on Twitter" className="text-[#7A7A7A] hover:text-[#1A1A1A]"><Twitter size={16} /></a>
          <a href="#" aria-label="Share on Facebook" className="text-[#7A7A7A] hover:text-[#1A1A1A]"><Facebook size={16} /></a>
          <a href="#" aria-label="Share on LinkedIn" className="text-[#7A7A7A] hover:text-[#1A1A1A]"><Linkedin size={16} /></a>
        </aside>

        <div className="max-w-2xl mx-auto md:mx-0">
          {post.content.map((p, i) => (
            <p
              key={i}
              className={`text-[17px] md:text-[18px] text-[#1A1A1A] leading-[1.8] mb-7 ${
                i === 0 ? "dropcap" : ""
              }`}
            >
              {p}
            </p>
          ))}

          <div className="flex flex-wrap gap-2 mt-10 pt-8 border-t border-[#a89e80]">
            {post.tags.map((t) => (
              <span key={t} className="bg-[#b8af92] text-[#4A4A4A] text-[11px] tracking-[0.22em] uppercase px-3 py-1">
                {t}
              </span>
            ))}
          </div>
        </div>
      </div>

      <PaisleyDivider className="my-20" />

      {/* Continue reading */}
      {others.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 md:px-12" data-testid="blog-continue-reading">
          <p className="eyebrow text-center">Continue reading</p>
          <h2 className="font-serif-display text-3xl md:text-4xl text-center mt-3 mb-12">
            More from the Journal
          </h2>
          <div className="grid md:grid-cols-3 gap-10">
            {others.map((p) => (
              <Link key={p.slug} to={`/blog/${p.slug}`} className="group block">
                <div className="aspect-[4/5] overflow-hidden bg-[#b8af92]">
                  <img src={p.cover} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1200ms]" />
                </div>
                <p className="eyebrow text-[#1A1A1A] mt-4">{p.category}</p>
                <h3 className="font-serif-display text-xl mt-2 leading-tight group-hover:text-[#1A1A1A] transition-colors">
                  {p.title}
                </h3>
              </Link>
            ))}
          </div>
        </section>
      )}
    </article>
  );
}
